/**
 * Cache Version Service
 * 
 * Provides smart content-based cache busting to replace aggressive Date.now() and Math.random()
 * Falls back to timestamp-based versioning when content versioning is unavailable
 */

import { conversationAPI, filesystemAPI } from './api';
import { logger } from '../utils/logger';

interface CacheVersionOptions {
  includeConversationVersion?: boolean;
  forceRefresh?: boolean;
  cacheDuration?: number; // in milliseconds, default 5 minutes
}

interface CachedVersion {
  version: string;
  timestamp: number;
}


export class CacheVersionService {
  private static instance: CacheVersionService;
  private readonly CACHE_PREFIX = 'cache_version_';
  private readonly DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private pendingRequests: Map<string, Promise<string>> = new Map();

  constructor() {
    // Singleton pattern
    if (CacheVersionService.instance) {
      return CacheVersionService.instance;
    }
    CacheVersionService.instance = this;
  }

  /**
   * Get version string for a file
   * Uses file modification time or conversation version as cache key
   * Falls back to timestamp if unavailable
   */
  async getFileVersion(
    filePath: string,
    conversationId?: string,
    options: CacheVersionOptions = {}
  ): Promise<string> {
    const {
      includeConversationVersion = false,
      forceRefresh = false,
      cacheDuration = this.DEFAULT_CACHE_DURATION,
    } = options;

    const cacheKey = this.getCacheKey(filePath, conversationId);

    // Check if we have a pending request for this file
    if (!forceRefresh && this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }

    // Check local cache first
    if (!forceRefresh) {
      const cached = this.getFromCache(cacheKey, cacheDuration);
      if (cached) {
        return cached;
      }
    }

    // Create promise for this request to handle concurrent calls
    const versionPromise = this.fetchFileVersion(
      filePath,
      conversationId,
      includeConversationVersion
    ).finally(() => {
      // Clean up pending request
      this.pendingRequests.delete(cacheKey);
    });

    this.pendingRequests.set(cacheKey, versionPromise);
    return versionPromise;
  }

  /**
   * Fetch version information from API
   */
  private async fetchFileVersion(
    filePath: string,
    conversationId?: string,
    includeConversationVersion: boolean = false
  ): Promise<string> {
    try {
      const versionParts: string[] = [];

      // Try to get file modification time
      try {
        const fileInfo = await filesystemAPI.info(filePath, conversationId);
        if (fileInfo.lastModified) {
          // Use last modified timestamp as version
          const modTime = new Date(fileInfo.lastModified).getTime();
          versionParts.push(modTime.toString());
        } else if (fileInfo.size !== undefined) {
          // Fallback to file size as a rough indicator
          versionParts.push(`size_${fileInfo.size}`);
        }
      } catch (error) {
        logger.debug(`Could not get file info for ${filePath}:`, error);
      }

      // Include conversation version if requested
      if (includeConversationVersion && conversationId) {
        try {
          const convVersion = await conversationAPI.getVersion(conversationId);
          if (convVersion.success && convVersion.version) {
            versionParts.push(convVersion.version);
          }
        } catch (error) {
          logger.debug(`Could not get conversation version for ${conversationId}:`, error);
        }
      }

      // Generate final version string
      let version: string;
      if (versionParts.length > 0) {
        version = versionParts.join('_');
      } else {
        // Fallback to timestamp, but use minute precision to allow some caching
        version = Math.floor(Date.now() / 60000).toString();
      }

      // Cache the version
      this.saveToCache(this.getCacheKey(filePath, conversationId), version);

      return version;
    } catch (error) {
      logger.error('Failed to get file version:', error);
      // Fallback to timestamp
      return Date.now().toString();
    }
  }

  /**
   * Generate URL with smart cache busting
   */
  async getResourceUrl(
    baseUrl: string,
    conversationId?: string,
    options: CacheVersionOptions = {}
  ): Promise<string> {
    try {
      // Extract file path from URL
      const urlObj = new URL(baseUrl, window.location.origin);
      const pathMatch = urlObj.pathname.match(/\/([^/?]+)(?:\?|$)/);
      const filePath = pathMatch ? pathMatch[1] : 'unknown';

      // Get version for this file
      const version = await this.getFileVersion(filePath, conversationId, options);

      // Add version to URL
      const separator = baseUrl.includes('?') ? '&' : '?';
      // Add both version and a session identifier for conversation isolation
      const sessionId = conversationId ? conversationId.substring(0, 8) : 'default';
      return `${baseUrl}${separator}v=${version}&s=${sessionId}`;
    } catch (error) {
      logger.error('Failed to generate versioned URL:', error);
      // Fallback to original URL with timestamp
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}v=${Date.now()}`;
    }
  }

  /**
   * Batch get versions for multiple files
   * More efficient than individual calls
   */
  async batchGetVersions(
    filePaths: string[],
    conversationId?: string,
    options: CacheVersionOptions = {}
  ): Promise<string[]> {
    const promises = filePaths.map(path =>
      this.getFileVersion(path, conversationId, options).catch(err => {
        logger.debug(`Failed to get version for ${path}:`, err);
        return Date.now().toString(); // Fallback for individual failures
      })
    );

    return Promise.all(promises);
  }

  /**
   * Clear all cached versions
   */
  clearCache(conversationId?: string): void {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(this.CACHE_PREFIX)) {
        if (!conversationId || key.includes(conversationId)) {
          localStorage.removeItem(key);
        }
      }
    });

    // Also clear pending requests when clearing cache
    if (conversationId) {
      // Clear any pending requests for this conversation
      this.pendingRequests.forEach((_promise, key) => {
        if (key.includes(conversationId)) {
          this.pendingRequests.delete(key);
        }
      });
    } else {
      // Clear all pending requests
      this.pendingRequests.clear();
    }

    logger.debug('🧹 Cleared cache for conversation:', conversationId || 'all');
  }

  /**
   * Get cache key for a file
   */
  private getCacheKey(filePath: string, conversationId?: string): string {
    const parts = [this.CACHE_PREFIX];
    if (conversationId) {
      parts.push(conversationId);
    }
    parts.push(filePath.replace(/[^a-zA-Z0-9]/g, '_'));
    return parts.join('_');
  }

  /**
   * Get version from local cache
   */
  private getFromCache(key: string, maxAge: number): string | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const data: CachedVersion = JSON.parse(cached);
      const age = Date.now() - data.timestamp;

      if (age < maxAge) {
        return data.version;
      }

      // Cache expired, remove it
      localStorage.removeItem(key);
      return null;
    } catch (error) {
      logger.debug('Cache read error:', error);
      return null;
    }
  }

  /**
   * Save version to local cache
   */
  private saveToCache(key: string, version: string): void {
    try {
      const data: CachedVersion = {
        version,
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      logger.debug('Cache write error:', error);
      // Ignore cache write errors (quota exceeded, etc.)
    }
  }

  /**
   * Static helper to get singleton instance
   */
  static getInstance(): CacheVersionService {
    if (!CacheVersionService.instance) {
      CacheVersionService.instance = new CacheVersionService();
    }
    return CacheVersionService.instance;
  }
}

// Export singleton instance
export const cacheVersionService = CacheVersionService.getInstance();